<script setup lang="ts">
    import { ref, computed } from 'vue'

    import { insertIntoTextArea1 } from '@common/Common'

    import type {
        ScoringAlternativeReadModel,
        ScoringAttributeReadModel,
        ScoringListReadModel,
        ScoringStatusReadModel
    } from '@Evaluation/AlternativesSurveys/Domain/readmodels'

    import
        {
            SurveyAlternativesManagerDialogSaveEventPayload,
            SurveyAlternativesManagerDialogViewModel
        } from '@Evaluation/AlternativesSurveys/Components/SurveyAlternativesManagerDialog'

    /* ---------------- props / emits ---------------- */

    const props = defineProps<{
        viewModel: SurveyAlternativesManagerDialogViewModel
    }>()

    const emit = defineEmits<{
        (e: 'load-alternatives', listId: string): void
        (e: 'preview-description', alternativeId: string, template: string | null): void
        (e: 'save-survey', payload: SurveyAlternativesManagerDialogSaveEventPayload): void
    }>()

    /* ---------------- shortcuts ---------------- */

    const vm = computed(() => props.viewModel)
    const opened = computed(() => vm.value?.opened ?? false)

    /* ---------------- derived (read-only) ---------------- */

    const initialized = computed(() => vm.value.initialized)
    const filtering = computed(() => vm.value.filtering)

    const alternativesLabel = computed(() => vm.value.alternativesLabel)
    const attributes = computed<ScoringAttributeReadModel[]>(() => vm.value.attributes)
    const statuses = computed<ScoringStatusReadModel[]>(() => vm.value.statuses)
    const lists = computed<ScoringListReadModel[]>(() => vm.value.lists)
    const filterLabel = computed(() => vm.value.filterLabel)

    const surveyId = computed(() => vm.value.surveyId)
    const surveyName = computed(() => vm.value.surveyName)

    const includedAlternatives = computed<ScoringAlternativeReadModel[]>(() => vm.value.includedAlternatives)
    const alternativesView = computed<ScoringAlternativeReadModel[]>(() => vm.value.alternativesView)
    const alternativeDescriptionPreview = computed(() => vm.value.alternativeDescriptionPreview)

    /* ---------------- writable computed (VM passthrough) ---------------- */

    const searchPhrase = computed<string | null>({
        get: () => vm.value.searchPhrase,
        set: val => { vm.value.searchPhrase = val }
    })

    const activeTab = computed<string>({
        get: () => vm.value.activeTab,
        set: val => { vm.value.activeTab = val }
    })

    const previewMode = computed<boolean>({
        get: () => vm.value.previewMode,
        set: val => { vm.value.previewMode = val }
    })

    const descriptionTemplate = computed<string | null>({
        get: () => vm.value.descriptionTemplate,
        set: val => { vm.value.descriptionTemplate = val }
    })

    /* ---------------- refs ---------------- */

    const descriptionTemplateTextArea = ref<HTMLTextAreaElement | null>(null)

    /* ---------------- helpers ---------------- */

    function insertIntoDescriptionTemplate(txt: string)
    {
        if (!descriptionTemplateTextArea.value) return

        insertIntoTextArea1(descriptionTemplateTextArea.value, `{${txt}}`)
        descriptionTemplateTextArea.value.dispatchEvent(new Event('input'))
    }

    function includeAll()
    {
        alternativesView.value.forEach(a => (a.IsIncluded = true))
    }

    function excludeAll()
    {
        alternativesView.value.forEach(a => (a.IsIncluded = false))
    }

    /* ---------------- filtering ---------------- */

    function clearFilter()
    {
        vm.value.clearFilter()
    }

    function filterIncluded()
    {
        vm.value.filterIncluded()
    }

    function filterExcluded()
    {
        vm.value.filterExcluded()
    }

    function filterByStatus(statusId: number, statusName: string)
    {
        vm.value.filterByStatus(statusId, statusName)
    }

    function filterByList(listId: string, listName: string)
    {
        vm.value.filterByList(listId, listName)
        emit('load-alternatives', listId)
    }

    /* ---------------- preview / save ---------------- */

    function showDescriptionPreview(alternativeId: string)
    {
        previewMode.value = true
        vm.value.alternativeDescriptionPreview = null
        emit('preview-description', alternativeId, descriptionTemplate.value)
    }

    function handleConfirm()
    {
        const payload = new SurveyAlternativesManagerDialogSaveEventPayload(
            surveyId.value,
            descriptionTemplate.value,
            includedAlternatives.value.map(a => a.AlternativeId)
        )

        emit('save-survey', payload)
    }

    /* ---------------- lifecycle ---------------- */

    function closeDialog()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content" style="height: calc(100vh - 50px);">
                    <div class="modal-header">
                        <h5 class="modal-title">Manage <span class="text-lowercase">{{alternativesLabel}}</span></h5>
                        <button @click="closeDialog" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h2 v-if="initialized === false"><i class="fa fa-spinner fa-spin"></i> Loading...</h2>
                        <div v-if="initialized">
                            <ul class="nav nav-tabs" role="tablist">
                                <li class="nav-item">
                                    <a class="nav-link" @click="activeTab = 'Alternatives'" :class="{'active': activeTab === 'Alternatives'}">Select</a>
                                </li>
                                <li class="nav-item">
                                    <a class="nav-link" @click="activeTab = 'Template'" :class="{'active': activeTab === 'Template'}">Define description</a>
                                </li>
                            </ul>

                            <div class="tab-content">
                                <div :class="{'active': activeTab === 'Alternatives'}" class="tab-pane pt-3">

                                    <div class="alert alert-info alert-dismissible" role="alert">
                                        <button type="button" class="close" data-dismiss="alert" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                        Select which <span class="text-lowercase italic">{{alternativesLabel}}</span> are included in your panel.
                                        You can use the filter or search to find the <span class="text-lowercase italic">{{alternativesLabel}}</span> you want to include,
                                        then check the <span class="text-lowercase italic">{{alternativesLabel}}</span>
                                        you want (one-by-one or in bulk using the gear-wheel).
                                        <strong>WARNING: New <span class="text-lowercase italic">{{alternativesLabel}}</span> won't be included in panels automatically.</strong>
                                    </div>

                                    <div class="row">
                                        <div class="col-md-6">
                                            <div class="dropdown">
                                                <a class="btn btn-outline-secondary btn-lg btn-block" data-toggle="dropdown">
                                                    Filter by: {{filterLabel ?? 'None'}}
                                                    <span class="caret"></span>
                                                </a>

                                                <div class="dropdown-menu" style="width: 100%; max-height: 250px; overflow-y: auto;">
                                                    <a class="dropdown-item" @click="clearFilter">No filter</a>
                                                    <div class="dropdown-divider"></div>

                                                    <a class="dropdown-item" @click="filterIncluded">Included in the survey</a>
                                                    <a class="dropdown-item" @click="filterExcluded">Not included in the survey</a>
                                                    <div class="dropdown-divider"></div>

                                                    <div class="dropdown-header">Filter by status:</div>
                                                    <a class="dropdown-item" v-for="s in statuses" :key="s.Id" @click="filterByStatus(s.Id, s.Name)">
                                                        {{s.Name}}
                                                    </a>
                                                    <div class="dropdown-divider"></div>

                                                    <div class="dropdown-header">Filter by list:</div>
                                                    <a class="dropdown-item" v-for="l in lists" :key="l.Id" @click="filterByList(l.Id, l.Name)">
                                                        {{l.Name}}
                                                    </a>

                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-md-6">
                                            <div class="input-group input-group-lg">
                                                <input type="search" placeholder="Search..." class="form-control" v-model="searchPhrase">
                                                <span class="input-group-append">
                                                    <span class="input-group-text"><i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i></span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <br />
                                    <h3 v-if="filtering === true">Loading...</h3>
                                    <table class="table table-bordered" v-if="filtering === false">
                                        <thead>
                                            <tr>
                                                <th style="width: 45px;" class="text-center">
                                                    <div class="dropdown">
                                                        <a class="text-primary" data-toggle="dropdown">
                                                            <i class="fa fa-cog fa-lg"></i>
                                                        </a>
                                                        <div class="dropdown-menu">
                                                            <a class="dropdown-item" @click="includeAll">Include all listed below</a>
                                                            <a class="dropdown-item" @click="excludeAll">Exclude all listed below</a>
                                                        </div>
                                                    </div>
                                                </th>
                                                <th>Name</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="a in alternativesView" :key="a.Id">
                                                <td class="text-center">
                                                    <a class="text-primary" @click="a.IsIncluded = !a.IsIncluded">
                                                        <i v-if="a.IsIncluded === true" class="fa-solid fa-square-check"></i>
                                                        <i v-if="a.IsIncluded === false" class="fa-regular fa-square"></i>
                                                    </a>
                                                </td>
                                                <td>
                                                    {{a.Name}}
                                                </td>
                                                <td>
                                                    {{a.StatusName}}
                                                </td>

                                            </tr>

                                        </tbody>
                                    </table>
                                </div>
                                <div :class="{'active': activeTab === 'Template' }" class="tab-pane pt-3">

                                    <div v-if="previewMode === true">

                                        <div class="d-flex justify-content-end">
                                            <button class="btn btn-sm btn-secondary" @click="previewMode = false">Edit template</button>

                                            <div class="dropdown ml-1" v-if="includedAlternatives?.length">
                                                <button class="btn btn-sm btn-outline-secondary dropdown-toggle" data-toggle="dropdown">Preview</button>
                                                <div class="dropdown-menu dropdown-menu-right" style="overflow-y: auto; max-height: 400px;">
                                                    <a class="dropdown-item" v-for="a in includedAlternatives" :key="a.Id" @click="showDescriptionPreview(a.Id)">
                                                        {{a.Name}}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>

                                        <div v-html="alternativeDescriptionPreview"></div>
                                    </div>

                                    <div v-if="previewMode === false">
                                        <div class="d-flex justify-content-end">
                                            <div class="dropdown">
                                                <button class="btn btn-sm btn-secondary dropdown-toggle" data-toggle="dropdown">Insert</button>
                                                <div class="dropdown-menu dropdown-menu-right" style="overflow-y: auto; max-height: 400px;">
                                                    <a class="dropdown-item" @click="insertIntoDescriptionTemplate('Description')">Description</a>
                                                    <a class="dropdown-item" @click="insertIntoDescriptionTemplate('Status')">Status</a>
                                                    <a class="dropdown-item" v-for="attr in attributes" :key="attr.Id" @click="insertIntoDescriptionTemplate(attr.Name)">
                                                        {{attr.Name}}
                                                    </a>
                                                </div>
                                            </div>

                                            <div class="dropdown ml-1" v-if="includedAlternatives?.length">
                                                <button class="btn btn-sm btn-outline-secondary dropdown-toggle" data-toggle="dropdown">Preview</button>
                                                <div class="dropdown-menu dropdown-menu-right" style="overflow-y: auto; max-height: 400px;">
                                                    <a class="dropdown-item" v-for="a in includedAlternatives" :key="a.Id" @click="showDescriptionPreview(a.Id)">
                                                        {{a.Name}}
                                                    </a>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="form-group">
                                            <textarea ref="descriptionTemplateTextArea" rows="5" v-model="descriptionTemplate" class="form-control"></textarea>
                                            <small class="form-text text-muted"></small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="handleConfirm" class="btn btn-primary">Save &amp; Close</button>
                        <button type="button" @click="closeDialog" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>